import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Matrix from "@/pages/Matrix";
import AIBuilder from "@/pages/AIBuilder";
import GapFinder from "@/pages/GapFinder";
import ImportPage from "@/pages/Import";
import ExportPage from "@/pages/Export";
import Platforms from "@/pages/Platforms";
import Admin from "@/pages/Admin";
import Inventory from "@/pages/Inventory";
import PricingPage from "@/pages/Pricing";
import Orders from "@/pages/Orders";
import Customers from "@/pages/Customers";
import Analytics from "@/pages/Analytics";
import Notifications from "@/pages/Notifications";
import Layout from "@/components/Layout";
import Jobs from "@/pages/Jobs";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-mono-data text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Layout>{children}</Layout>;
}

function OnboardingGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/dashboard" replace />;
  return children;
}

function RazorpayLoader() {
  useEffect(() => {
    if (window.Razorpay) return;
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);
  return null;
}

export default function App() {
  useEffect(() => { document.title = "EcomKit — One catalog, every marketplace"; }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RazorpayLoader />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={<OnboardingGate><Onboarding /></OnboardingGate>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/products" element={<Protected><Products /></Protected>} />
            <Route path="/matrix" element={<Protected><Matrix /></Protected>} />
            <Route path="/ai-builder" element={<Protected><AIBuilder /></Protected>} />
            <Route path="/gap-finder" element={<Protected><GapFinder /></Protected>} />
            <Route path="/import" element={<Protected><ImportPage /></Protected>} />
            <Route path="/export" element={<Protected><ExportPage /></Protected>} />
            <Route path="/platforms" element={<Protected><Platforms /></Protected>} />
            <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
            <Route path="/pricing" element={<Protected><PricingPage /></Protected>} />
            <Route path="/orders" element={<Protected><Orders /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
